import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { FileText, X, Calculator, IndianRupee, Calendar } from "lucide-react";
import toast from "react-hot-toast";
import { statesGST } from "../../utils/util";
import { useDispatch } from "react-redux";
import {
  createQuotation,
  downloadInvoice,
} from "../../redux/reduxSlices/quotationSlice";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { Controller } from "react-hook-form";
import moment from "moment";

const ProformaInvoice = ({
  onClose,
  onSuccess,
  orderNo = "",
  name = "",
  address = "",
  clientCompanyName = "",
  gstNo = "",
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [calculations, setCalculations] = useState({
    sgst: 0,
    cgst: 0,
    igst: 0,
    total: 0,
    roundOff: 0,
    roundedTotal: 0,
  });
  const [selectedState, setSelectedState] = useState(null);
  const [quotationId, setQuotationId] = useState("");
  const [showDownloadButton, setShowDownloadButton] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isIgst, setIsIgst] = useState(false);

  const dispatch = useDispatch();

  // Get today's date in YYYY-MM-DD format for min attribute
  const getTodayFormatted = () => {
    return moment().format("DD-MM-YYYY");
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
    setValueAs,
    control,
  } = useForm({
    defaultValues: {
      orderNo: orderNo,
      clientName: name,
      clientAddress: address,
      companyName: clientCompanyName,
      state: "",
      gstCode: "",
      gstNumber: gstNo,
      invoiceNo: "",
      productName: "",
      amount: "",
      date: moment().format("DD-MM-YYYY"),
    },
  });

  const watchAmount = watch("amount");
  const watchState = watch("state");

  useEffect(() => {
    if (watch("gstCode")) {
      if (watch("gstCode") == 27) {
        setIsIgst(false);
      } else {
        setIsIgst(true);
      }
    }
  }, [watch("gstCode")]);

  // Update selected state and GST code when state changes
  useEffect(() => {
    if (watchState) {
      const foundState = statesGST.find((item) => item.state === watchState);
      if (foundState) {
        setSelectedState(foundState);
        setValue("gstCode", foundState.gstCode);
      }
    } else {
      setSelectedState(null);
      setValue("gstCode", "");
    }
  }, [watchState, setValue]);

  // Calculate taxes whenever amount changes
  useEffect(() => {
    const amount = parseFloat(watchAmount) || 0;

    const sgst = amount * 0.09;
    const cgst = amount * 0.09;
    const igst = amount * 0.18;

    const total = !isIgst ? amount + sgst + cgst : amount + igst;

    const roundedTotal = Math.round(total);
    const roundOff = roundedTotal - total;

    setCalculations({
      sgst: sgst.toFixed(2),
      cgst: cgst.toFixed(2),
      igst: igst.toFixed(2),
      total: total.toFixed(2),
      roundOff: roundOff.toFixed(2),
      roundedTotal: roundedTotal.toFixed(2),
    });
  }, [watchAmount, isIgst]);

  const onSubmitForm = async (data) => {
    setIsLoading(true);

    try {
      // Add tax calculations and GST code to the form data
      const payload = {
        ...data,
        amount: parseFloat(data.amount),
        sgst: calculations.sgst,
        cgst: calculations.cgst,
        igst: calculations.igst,
        total: calculations.total,
        roundedTotal: calculations.roundedTotal,
        roundOff: calculations.roundOff,
        isIgst: isIgst,
        // GST code is automatically included from the form data
      };

      // console.log("Proforma Invoice Data:", payload);
      const response = await dispatch(createQuotation(payload)).unwrap();
      //   return

      toast.success("Proforma invoice created successfully!");
      if (response.success) {
        setQuotationId(response.quotation_id);
        setShowDownloadButton(true);
      }

      // Call onSuccess callback if provided
      //   if (onSuccess) {
      //     onSuccess(payload);
      //   }

      // Close the modal
      //   if (onClose) {
      //     onClose();
      //   }
    } catch (err) {
      toast.error(err?.message || "Something went wrong");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const downloadPDF = async () => {
    if (!quotationId) {
      toast.error("No quotation ID available");
      return;
    }

    setIsDownloading(true);
    try {
      const result = await dispatch(downloadInvoice(quotationId)).unwrap();

      // Check if result is a string (PDF content as string)
      if (typeof result === "string" && result.startsWith("%PDF")) {
        // Convert string to Blob
        const blob = new Blob([result], { type: "application/pdf" });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice_${quotationId}.pdf`;
        document.body.appendChild(a);
        a.click();

        // Clean up
        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);

        toast.success("PDF downloaded successfully!");
      }
      // Check if result is already a Blob
      else if (result instanceof Blob) {
        const url = window.URL.createObjectURL(result);
        const a = document.createElement("a");
        a.href = url;
        a.download = `invoice_${quotationId}.pdf`;
        document.body.appendChild(a);
        a.click();

        setTimeout(() => {
          document.body.removeChild(a);
          window.URL.revokeObjectURL(url);
        }, 100);

        toast.success("PDF downloaded successfully!");
      } else {
        console.error("Unexpected response type:", typeof result);
        toast.error("Invalid response format from server");
      }
    } catch (error) {
      console.error("Download error:", error);
      toast.error(error?.message || "Failed to download PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center p-2 z-50">
      <div
        className="bg-white rounded-lg border border-gray-200 w-full max-w-2xl max-h-[95vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-base font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-4 h-4 text-orange-600" />
            New Proforma Invoice
          </h2>
          <button
            type="button"
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmitForm)} className="p-4">
          <div className="space-y-2">
            {/* Basic Information Section */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                Invoice Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Invoice Number and Date - Two in one row */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Invoice Number *
                  </label>
                  <input
                    type="text"
                    {...register("invoiceNo", {
                      required: "Invoice number is required",
                    })}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.invoiceNo
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="INV-2024-001"
                    disabled={isLoading}
                  />
                  {errors.invoiceNo && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.invoiceNo.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Date *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 z-10" />
                    <Controller
                      control={control}
                      name="date"
                      rules={{ required: "Date is required" }}
                      render={({ field }) => (
                        <DatePicker
                          selected={
                            field.value
                              ? new Date(
                                  field.value.split("-").reverse().join("-"),
                                )
                              : null
                          }
                          onChange={(date) => {
                            if (date) {
                              const day = String(date.getDate()).padStart(
                                2,
                                "0",
                              );
                              const month = String(
                                date.getMonth() + 1,
                              ).padStart(2, "0");
                              const year = date.getFullYear();
                              field.onChange(`${day}-${month}-${year}`);
                            } else {
                              field.onChange("");
                            }
                          }}
                          minDate={new Date()} // This restricts dates before today
                          dateFormat="dd/MM/yyyy"
                          placeholderText="DD/MM/YYYY"
                          className={`w-full pl-8 pr-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                            errors.date
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                          }`}
                          disabled={isLoading}
                        />
                      )}
                    />
                  </div>
                  {errors.date && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.date.message}
                    </p>
                  )}
                </div>

                {/* <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Order No 
                  </label>
                  <input
                    type="text"
                    {...register("orderNo")}
                    // defaultValue={orderNo}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.orderNo
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter order no"
                  />
                  {errors.orderNo && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.orderNo.message}
                    </p>
                  )}
                </div> */}
                {/* Client Name and Company Name - Two in one row */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Client Name *
                  </label>
                  <input
                    type="text"
                    {...register("clientName", {
                      required: "Client name is required",
                      minLength: {
                        value: 2,
                        message: "Minimum 2 characters",
                      },
                      setValueAs: (value) => value?.toUpperCase(),
                    })}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    // defaultValue={name}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.clientName
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter client name"
                    disabled={isLoading}
                  />
                  {errors.clientName && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.clientName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    {...register("companyName", {
                      required: "Company name is required",
                    })}
                    // defaultValue={clientCompanyName}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.companyName
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter company name"
                    disabled={isLoading}
                  />
                  {errors.companyName && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.companyName.message}
                    </p>
                  )}
                </div>

                {/* Client Address - Full width */}
                <div className="md:col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Client Address *
                  </label>
                  <textarea
                    {...register("clientAddress", {
                      required: "Client address is required",
                    })}
                    // defaultValue={address}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.clientAddress
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter complete address"
                    rows="3"
                    disabled={isLoading}
                  />
                  {errors.clientAddress && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.clientAddress.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Payment Note
                  </label>
                  <input
                    type="text"
                    {...register("paymentNote")}
                    // defaultValue={clientCompanyName}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.paymentNote
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter payment note"
                    disabled={isLoading}
                  />
                  {errors.paymentNote && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.paymentNote.message}
                    </p>
                  )}
                </div>

                {/* State and Product Name - Two in one row */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    State *
                  </label>
                  <select
                    {...register("state", {
                      required: "State is required",
                    })}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.state
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    disabled={isLoading}
                  >
                    <option value="">Select State</option>
                    {statesGST.map((item) => (
                      <option key={item.gstCode} value={item.state}>
                        {item.state} ({item.gstCode})
                      </option>
                    ))}
                  </select>
                  {errors.state && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.state.message}
                    </p>
                  )}
                </div>

                {/* <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Product Name *
                  </label>
                  <input
                    type="text"
                    {...register("productName", {
                      required: "Product name is required",
                    })}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.productName
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="Enter product/service name"
                    disabled={isLoading}
                  />
                  {errors.productName && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.productName.message}
                    </p>
                  )}
                </div> */}

                {/* Hidden input for GST Code */}
                <input type="hidden" {...register("gstCode")} />

                {/* Display selected GST Code (optional) */}
                {selectedState && (
                  <div className="md:col-span-2 mt-1">
                    <p className="text-xs text-gray-500">
                      Selected GST Code:{" "}
                      <span className="font-medium text-gray-700">
                        {selectedState.gstCode}
                      </span>
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Amount and Tax Calculation Section */}
            <section>
              <h3 className="text-sm font-semibold text-gray-900 mb-3 pb-1 border-b">
                Amount & Tax Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {/* Amount */}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    Amount (₹) *
                  </label>
                  <div className="relative">
                    <IndianRupee className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      {...register("amount", {
                        required: "Amount is required",
                        pattern: {
                          value: /^\d*\.?\d*$/,
                          message: "Please enter a valid number",
                        },
                      })}
                      className={`w-full pl-7 pr-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                        errors.amount
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                      }`}
                      placeholder="0.00"
                      disabled={isLoading}
                      onKeyPress={(e) => {
                        if (!/[0-9.]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                    />
                  </div>
                  {errors.amount && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.amount.message}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">
                    GST Number (Optional)
                  </label>
                  <input
                    type="text"
                    {...register("gstNumber", {
                      pattern: {
                        value:
                          /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/,
                        message:
                          "Please enter a valid GST number (e.g., 27AAPFU0939F1Z5)",
                      },
                      setValueAs: (value) => value?.toUpperCase(),
                    })}
                    // defaultValue={gstNo}
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase();
                    }}
                    className={`w-full px-2.5 py-1.5 border rounded text-sm focus:outline-none focus:ring-1 focus:ring-orange-500 ${
                      errors.gstNumber
                        ? "border-red-300 bg-red-50"
                        : "border-gray-300"
                    }`}
                    placeholder="27AAPFU0939F1Z5"
                    disabled={isLoading}
                  />
                  {errors.gstNumber && (
                    <p className="text-red-600 text-xs mt-1">
                      {errors.gstNumber.message}
                    </p>
                  )}
                </div>
                {/* Tax Calculations Display */}
                {watchAmount && (
                  <div className="md:col-span-2 mt-2">
                    <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                      <h4 className="text-xs font-semibold text-gray-700 mb-3 flex items-center gap-1">
                        <Calculator className="w-3.5 h-3.5" />
                        Tax Calculation Summary
                      </h4>

                      <div className="space-y-2">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Base Amount:</span>
                          <span className="font-medium">
                            ₹ {parseFloat(watchAmount || 0).toFixed(2)}
                          </span>
                        </div>

                        {isIgst ? (
                          <div className="flex justify-between text-sm">
                            <span className="text-gray-600">IGST (9%):</span>
                            <span className="font-medium">
                              ₹ {calculations.igst}
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">SGST (9%):</span>
                              <span className="font-medium">
                                ₹ {calculations.sgst}
                              </span>
                            </div>

                            <div className="flex justify-between text-sm">
                              <span className="text-gray-600">CGST (9%):</span>
                              <span className="font-medium">
                                ₹ {calculations.cgst}
                              </span>
                            </div>
                          </>
                        )}
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Round Off:</span>
                          <span className="font-medium">
                            ₹ {calculations.roundOff}
                          </span>
                        </div>

                        <div className="border-t border-gray-200 my-2 pt-2">
                          <div className="flex justify-between text-base font-semibold">
                            <span className="text-gray-900">Total Amount:</span>
                            <span className="text-orange-600">
                              ₹ {calculations.roundedTotal}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </section>
          </div>

          {/* Form Actions */}
          {/* Form Actions */}
          <div className="flex items-center justify-end gap-2 mt-6 border-t border-gray-200 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm font-medium"
              disabled={isLoading}
            >
              Cancel
            </button>

            {!showDownloadButton ? (
              <button
                type="submit"
                className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full inline-block mr-1.5"></div>
                    Creating...
                  </>
                ) : (
                  <>
                    <FileText className="w-3.5 h-3.5 inline-block mr-1.5" />
                    Create Invoice
                  </>
                )}
              </button>
            ) : (
              <button
                type="button" // Changed from "submit" to "button"
                onClick={downloadPDF} // Fixed: removed parentheses
                className="px-4 py-2 rounded text-white text-sm font-medium bg-gradient-to-br from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={isDownloading}
              >
                {isDownloading ? (
                  <>
                    <div className="animate-spin h-3.5 w-3.5 border-2 border-white border-t-transparent rounded-full inline-block mr-1.5"></div>
                    Downloading...
                  </>
                ) : (
                  <>
                    <FileText className="w-3.5 h-3.5 inline-block mr-1.5" />
                    Download PDF
                  </>
                )}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProformaInvoice;
